'use strict';

/**
 * Client side of the wishlist login gate.
 *
 * This is a user-experience control only. The enforcement that matters lives on
 * `Wishlist-AddProduct`, which rejects guest saves regardless of what the
 * browser does.
 *
 * @module wishlist/wishlistGate
 */

// `.default` because WishlistGateModal is an ES module default export while
// this module stays CommonJS for its named-property consumers. Mixing `import`
// with `module.exports` in one file is a webpack error.
var WishlistGateModal = require('../components/common/WishlistGateModal').default;
var ToastMessage = require('../components/common/ToastMessage');

/**
 * Configuration published by the APAC page footer. Absent everywhere else,
 * which is what keeps the gate inert outside the markets it is enabled for.
 *
 * @returns {Object} the gate configuration, possibly empty
 */
function settings() {
    return (window.sitePreferences && window.sitePreferences.wishlistGate) || {};
}

/**
 * Whether this visitor must authenticate before saving.
 *
 * @returns {boolean} true when the prompt should replace the save
 */
function isRequired() {
    return settings().required === true;
}

/**
 * The CSRF field the gate's three POST routes validate.
 *
 * Returned as an object so it can be merged straight into an `$.ajax` payload.
 * Empty when the page published no token, which leaves the request to be
 * refused by the route rather than silently skipped here: a save that cannot
 * be validated must not look like one that worked.
 *
 * @returns {Object} the token field, or an empty object
 */
function csrfField() {
    var config = settings();

    if (!config.csrfTokenName || !config.csrfToken) {
        return {};
    }

    var field = {};
    field[config.csrfTokenName] = config.csrfToken;
    return field;
}

/**
 * Works out which surface a save was triggered from, so the prompt and the
 * confirmation can follow the per-surface content rules.
 *
 * @param {Object} $el - jQuery element that was clicked
 * @returns {string} one of plp, pdp, search, sto
 */
function detectSurface($el) {
    if ($el.closest('.js-suggestions, .b-suggestions').length) {
        return 'search';
    }
    if ($el.closest('.shop-this-outfit-models').length) {
        return 'sto';
    }
    if ($el.closest('.product-detail').length && !$el.closest('.wishlistTile').length) {
        return 'pdp';
    }
    return 'plp';
}

/**
 * Discards whatever the gate is holding, because the customer dismissed the
 * prompt rather than authenticating.
 *
 * Fire and forget: there is nothing useful to do with the answer, and the
 * prompt is already closing. If the request never lands the record simply
 * expires on its own timer, which is the behaviour this replaces.
 *
 * @returns {void}
 */
function cancelPendingAction() {
    var config = settings();

    if (!config.cancelIntentUrl) {
        return;
    }

    $.ajax({
        url: config.cancelIntentUrl,
        type: 'post',
        dataType: 'json',
        data: csrfField()
    });
}

/**
 * Whether the route answered that this visitor did not need the gate at all.
 *
 * Both intent routes refuse a request that is not gated, and they distinguish
 * the two reasons: `notRequired` for a customer who is already authenticated,
 * `gateDisabled` for a market the kill switch has turned off. Either way the
 * page that intercepted the click was out of date and the prompt would be a
 * dead end.
 *
 * @param {Object} data - the server payload
 * @returns {boolean} true when the gate does not apply to this visitor
 */
function gateDoesNotApply(data) {
    return !!data && (data.notRequired === true || data.gateDisabled === true);
}

/**
 * Stands the gate down for the rest of this page's life.
 *
 * The page published `required: true` and the server has just contradicted it,
 * which happens when the customer authenticated in another tab or the kill
 * switch was thrown after the page was rendered. Clearing the flag locally is
 * what stops the next click being intercepted into the same dead end, and it
 * is also what makes a replayed click go through instead of looping.
 *
 * @returns {void}
 */
function standDown() {
    var config = settings();
    config.required = false;
    config.enabled = false;
}

/**
 * Records the pending save, then opens the prompt.
 *
 * The intent is created server-side before the customer leaves for the hosted
 * authentication UI, because nothing about the attempt survives the round trip
 * otherwise.
 *
 * @param {Object} context - what the customer was trying to save
 * @returns {void}
 */
function promptForAuthentication(context) {
    var config = settings();

    if (!config.createIntentUrl || !config.promptUrl) {
        return;
    }

    // The prompt is shown whether or not the intent was stored. A guest who
    // cannot be given a continuation still needs to be told why their save did
    // not happen, and still needs a way to sign in.
    var prompt = function () {
        new WishlistGateModal({
            contentUrl: config.promptUrl,
            surface: context.surface
        }).open();
    };

    $.ajax({
        url: config.createIntentUrl,
        type: 'post',
        dataType: 'json',
        data: $.extend({
            surface: context.surface,
            actionType: context.actionType,
            pid: context.pid,
            optionId: context.optionId || '',
            optionVal: context.optionVal || '',
            variantColor: context.variantColor || '',
            uuid: context.uuid || '',
            originUrl: window.location.pathname + window.location.search
        }, csrfField()),
        success: function (data) {
            if (!gateDoesNotApply(data)) {
                prompt();
                return;
            }

            // Not gated after all. Prompting an authenticated customer to sign
            // in would be a dead end, because `intercept` has already swallowed
            // the click: they would have to close the prompt and try again.
            // Standing the gate down and replaying the click completes the save
            // they asked for. `standDown` is what keeps the replay from being
            // intercepted a second time.
            standDown();

            if (context.$el && context.$el.length) {
                context.$el.trigger('click');
                return;
            }

            prompt();
        },
        error: prompt
    });
}

/**
 * Opens the prompt on its own, with no pending save behind it.
 *
 * Used where the customer asked for the saved-items list rather than to save a
 * product. No intent is created: `wishlistIntentHelpers.create` requires a
 * product id and a known surface, and there is neither here — nothing is
 * waiting to be executed once the customer authenticates.
 *
 * @returns {void}
 */
function openPrompt() {
    var config = settings();

    if (!config.promptUrl) {
        return;
    }

    new WishlistGateModal({
        contentUrl: config.promptUrl,
        surface: null
    }).open();
}

/**
 * Prompts a customer who asked for the saved-items list.
 *
 * The return is recorded server-side before the prompt opens, for the same
 * reason a save intent is: nothing about the attempt survives the hosted-UI
 * round trip otherwise, and the customer asked for the list, so that is where
 * authenticating should leave them.
 *
 * The prompt opens whether or not the record was stored. A customer who cannot
 * be given a return still needs to be told why they did not get the list.
 *
 * @param {string} [targetUrl] - where the prevented click was heading, used
 *   only if the server answers that this visitor is not gated after all
 * @returns {void}
 */
function promptForList(targetUrl) {
    var config = settings();

    if (!config.promptUrl) {
        return;
    }

    if (!config.createListIntentUrl) {
        openPrompt();
        return;
    }

    $.ajax({
        url: config.createListIntentUrl,
        type: 'post',
        dataType: 'json',
        data: csrfField(),
        success: function (data) {
            if (!gateDoesNotApply(data)) {
                openPrompt();
                return;
            }

            // Not gated after all, and the click that got here was prevented.
            // Sending the customer to the list they asked for is the whole
            // point of the link; prompting them to sign in when they already
            // are would strand them on the current page.
            standDown();
            window.location.href = targetUrl || config.listUrl || window.location.href;
        },
        error: function () {
            openPrompt();
        }
    });
}

/**
 * Intercepts a save attempt.
 *
 * @param {Object} $el - the element that was clicked
 * @param {Object} context - product and action details
 * @returns {boolean} true when the save was intercepted and must not proceed
 */
function intercept($el, context) {
    if (!isRequired()) {
        return false;
    }

    promptForAuthentication($.extend({
        surface: detectSurface($el),
        // Carried so a stale-gate answer can replay the click instead of
        // prompting a customer who does not need to authenticate.
        $el: $el
    }, context));

    return true;
}

/**
 * Handles a save the server refused because the visitor is not authenticated.
 *
 * `Wishlist-AddProduct` answers a gated guest with HTTP 200 and
 * `authenticationRequired: true`, so the refusal arrives in jQuery's success
 * handler rather than its error handler and is easy to walk straight past. Left
 * unhandled it is worse than an error: the optimistic filled heart stays
 * filled, and the customer is told nothing at all about a save that did not
 * happen.
 *
 * `intercept()` normally stops these attempts before any request is made, but
 * it can only act on what the page told it, and the page can be out of date.
 * A session that expired between load and click is one way; a page served from
 * the page cache whose gate state was rendered for somebody else is another.
 * Treating the server's refusal as a second trigger for the prompt makes the
 * customer's experience identical in all three cases, so correctness no longer
 * depends on the page being fresh.
 *
 * @param {Object} data - the server payload
 * @param {Object} $el - the element that was clicked
 * @param {Object} context - product and action details
 * @returns {boolean} true when the refusal was handled and the caller must stop
 */
function handleAuthenticationRequired(data, $el, context) {
    if (!data || data.authenticationRequired !== true) {
        return false;
    }

    promptForAuthentication($.extend({
        surface: detectSurface($el),
        $el: $el
    }, context));

    return true;
}

/**
 * Handles a bulk save the server refused, for either of the two reasons it
 * can refuse one.
 *
 * `Cart-SaveAllForLater` saves every out-of-stock line at once, so there is no
 * single product to record and nothing for the continuation to execute. The
 * prompt is opened with no intent behind it; the lines are still in the basket
 * afterwards, so the customer can repeat the action once they are signed in.
 *
 * The server's refusal is the only trigger, deliberately. The bulk control
 * shares one delegated handler with the "remove all" control beside it, so a
 * pre-emptive intercept would have to tell the two apart by URL or by a marker
 * class added to a template shared with every other region. The refusal
 * already distinguishes them, and it is the trigger that holds when the page's
 * gate state is stale.
 *
 * @param {Object} data - the server payload
 * @returns {boolean} true when the refusal was handled and the caller must stop
 */
function handleBulkSaveRefusal(data) {
    if (!data) {
        return false;
    }

    if (data.authenticationRequired === true) {
        openPrompt();
        return true;
    }

    // Refused for a reason signing in cannot fix: the set matched no basket
    // line, or it was larger than the cap the record accepts. The route says
    // so rather than promising a continuation it did not queue — but the
    // caller's only other branch is a redirect, so without this the
    // availability modal closes and the customer is told nothing. That silence
    // is the failure reported against the first bulk fix, on the very branch
    // that fix added.
    //
    // Scoped to storefronts the gate publishes configuration for, and to
    // payloads with no redirect of their own. Elsewhere this returns false and
    // the caller keeps the handling it has always had.
    var config = settings();
    if (data.error !== true || !config.errorMessage || data.redirectURL) {
        return false;
    }

    // eslint-disable-next-line no-use-before-define
    showFailure();

    return true;
}

/**
 * Anchors the confirmation to the bottom edge of the site header and to the
 * header's wishlist icon.
 *
 * DES-03 = A places the panel immediately below the header, not over it. The
 * header is `position: fixed` and its height varies with the breakpoint and
 * with whether the utility row is present, so the offset is measured instead
 * of hard-coded — `$('.js-header').outerHeight()` is the same measurement
 * eighteen other modules in this cartridge already use to clear it.
 *
 * Horizontally every desktop frame in the design ends the panel in the gap
 * between the wishlist icon and the bag icon beside it, so the minicart's
 * left edge is what the panel's right edge is measured from. It is measured
 * rather than restated as a gutter because the header's icons sit inside the
 * header's grid container: their distance from the viewport edge is the
 * container's gutter above the width the container caps at and a fixed inset
 * below it, and one measurement covers both. The wishlist icon's own box is
 * not the anchor — the heart is drawn by an icon-font pseudo-element that
 * overhangs the box to the right, so the box's right edge lands at the
 * heart's left edge, about 35px short of where the design ends the panel
 * (SFCC-7412).
 *
 * The values go into custom properties rather than onto `style.top` and
 * `style.right` so that the stylesheet keeps control: inline values would win
 * over the media query that anchors the panel to the bottom of the viewport
 * across its full width on mobile.
 *
 * @param {HTMLElement} element - the rendered toast element
 * @returns {void}
 */
function anchorBelowHeader(element) {
    if (!element || !element.style) {
        return;
    }

    var headerHeight = $('.js-header').outerHeight();

    if (headerHeight) {
        element.style.setProperty('--wishlist-confirmation-top', headerHeight + 'px');
    }

    // `:visible` because the phone header renders its own copy of the utility
    // row, and a hidden element measures as a zero-width box at the document
    // origin — which would read as a gap the width of the viewport and throw
    // the panel off screen.
    var minicart = $('.b-header-minicart:visible').get(0);
    var minicartBox = minicart && minicart.getBoundingClientRect ? minicart.getBoundingClientRect() : null;

    if (minicartBox && minicartBox.width && minicartBox.left > 0 && minicartBox.left <= window.innerWidth) {
        element.style.setProperty('--wishlist-confirmation-right', (window.innerWidth - minicartBox.left) + 'px');
    }
}

/**
 * Classes for the confirmation's backdrop.
 *
 * The shared `.modal-backdrop` carries the scrim colour and the full-viewport
 * box, and `.overlay` carries the stacking value that clears the fixed header.
 * The modifier only scopes visibility to the breakpoint, which the stylesheet
 * owns.
 */
var BACKDROP_CLASS = 'modal-backdrop overlay m-wishlist_save';

/**
 * The modifier the server puts on a panel whose request moved one item, and the
 * root class beside it. Both names are matched so the strip below cannot touch
 * anything else in the panel.
 */
var SINGLE_SAVE_ROOT = 'b-wishlist_confirmation m-single_save';

/**
 * The confirmation currently on screen, or null when none is.
 *
 * Module state rather than a DOM query because the answer has to be read
 * before `ToastMessage.show` displaces the previous panel, and because every
 * route out of a panel -- the fade animation, the fallback timer, the close
 * button, `destroy()`, and a later message asking the displaced instance to
 * remove itself -- runs the wrapped `remove` below, which clears this.
 */
var activeConfirmation = null;

/**
 * Removes any confirmation backdrop left in the document.
 *
 * Needed as a sweep as well as through the teardown below, because
 * `ToastMessage.show` discards a previous message by removing its element
 * outright rather than by calling `remove()` on the instance that owns it.
 *
 * @returns {void}
 */
function clearBackdrops() {
    var existing = document.querySelectorAll('.modal-backdrop.m-wishlist_save');

    for (var i = 0; i < existing.length; i += 1) {
        if (existing[i].parentNode) {
            existing[i].parentNode.removeChild(existing[i]);
        }
    }
}

/**
 * Puts the shared modal backdrop in the document behind the confirmation.
 *
 * Appended on every breakpoint and hidden by the stylesheet above 1023, so
 * this never needs to know the breakpoint and cannot fall out of step with a
 * resize. A hidden element takes no clicks, so scoping the dismissal comes
 * free with scoping the visibility.
 *
 * @returns {HTMLElement} the appended backdrop
 */
function appendBackdrop() {
    var backdrop = document.createElement('div');

    clearBackdrops();
    backdrop.className = BACKDROP_CLASS;
    document.body.appendChild(backdrop);

    return backdrop;
}

/**
 * Shows the save confirmation.
 *
 * A second save while the panel is still up replaces it rather than stacking
 * beside it, and the replacement restarts the five seconds (FR-17): the panel
 * goes away only once five seconds pass with no further save. That is
 * `ToastMessage.show`'s own behaviour -- it asks the previous message's owning
 * instance to remove itself, which clears that instance's timer and its
 * backdrop, then arms a fresh timer for the new element -- so there is no
 * timer or aggregation state kept here. The count climbs because the server
 * recomputes it from the list on every save, not because the client counts.
 *
 * What is decided here is whether that count is shown at all. FR-17 shows it
 * only when several items were added inside the five seconds, and the server
 * can see only half of that: it knows whether the one request it answered
 * moved several items, which is the bulk "save all for later" case, and marks
 * the panel `m-single_save` when it did not. The other half is this replacement
 * itself -- a panel arriving while one is still up is the second save in the
 * window -- so the mark comes off here. The markup string is edited rather than
 * the rendered node because the constructor bakes the string into the element.
 *
 * @param {Object} data - server payload carrying the rendered confirmation
 * @returns {void}
 */
function showConfirmation(data) {
    if (!data || !data.confirmationTemplate) {
        return;
    }

    var markup = activeConfirmation
        ? data.confirmationTemplate.replace(SINGLE_SAVE_ROOT, 'b-wishlist_confirmation')
        : data.confirmationTemplate;

    var toast = new ToastMessage(markup, {
        duration: 5000,
        type: 'success',
        modifier: 'm-wishlist_save',
        closable: true,
        closeLabel: settings().closeLabel || 'Close'
    });

    var backdrop = appendBackdrop();
    var dismiss = toast.remove.bind(toast);

    // Re-anchors the panel on resize so it tracks the header's icons as the
    // viewport narrows or widens. Without this the panel stays at the position
    // measured when it was shown while the icons move with the header's grid
    // container, and the two drift apart (SFCC-7486).
    var reanchor = function () {
        anchorBelowHeader(toast.element);
    };

    // Wrapping `remove` is what makes a sibling node safe here. Every
    // dismissal path already funnels through it — the fade animation, the
    // fallback timer, the close button and `destroy()` — so there is no route
    // by which the panel goes away and leaves the scrim covering the page, and
    // there is no route by which the resize listener stays bound after it.
    toast.remove = function () {
        if (activeConfirmation === toast) {
            activeConfirmation = null;
        }
        window.removeEventListener('resize', reanchor);
        backdrop.removeEventListener('click', toast.remove);
        if (backdrop.parentNode) {
            backdrop.parentNode.removeChild(backdrop);
        }
        dismiss();
    };

    backdrop.addEventListener('click', toast.remove);
    window.addEventListener('resize', reanchor);

    toast.show();
    // After `show`, which asks the panel this one displaces to remove itself
    // and so runs the clause above for it. Recorded before that, this would be
    // cleared by the very panel it replaced.
    activeConfirmation = toast;
    anchorBelowHeader(toast.element);
}

/**
 * Shows the FR-13 error: the save did not happen, the customer is told, and the
 * save control is left unfilled so a retry is a deliberate act.
 *
 * @returns {void}
 */
function showFailure() {
    var config = settings();

    if (!config.errorMessage) {
        return;
    }

    new ToastMessage(config.errorMessage, {
        duration: 5000,
        type: 'error',
        closable: true,
        closeLabel: config.closeLabel || 'Close'
    }).show();
}

/**
 * Undoes a wishlist copy whose cart removal failed.
 *
 * "Save for Later" copies the item to the saved list and then removes it from
 * the cart as a separate call. If the removal fails the item is in both places,
 * which FR-08 forbids. Rolling the copy back returns the customer to their
 * pre-attempt state, so the outcome is always either "the item moved" or
 * "nothing changed".
 *
 * @param {string} pid - product that was copied to the saved list
 * @returns {void}
 */
function rollbackSaveForLater(pid) {
    var config = settings();

    if (!config.rollbackUrl || !pid) {
        return;
    }

    $.ajax({
        url: config.rollbackUrl,
        type: 'post',
        dataType: 'json',
        data: $.extend({ pid: pid }, csrfField()),
        success: function (data) {
            // The panel on screen was painted from the copy this call has just
            // undone, so it is listing an item the customer no longer has
            // anywhere. Repainting it from the answer is what makes "nothing
            // changed" true on screen as well as in the data.
            //
            // Guarded on the payload rather than on the transport: a failed
            // rollback answers HTTP 200 with `error: true` and no markup, and
            // emptying the panel for that would delete the customer's whole
            // saved list from the page.
            if (!data || !data.renderedTemplate) {
                return;
            }

            $(document).find('.cart-saved-items').empty().html(data.renderedTemplate);
            $('body').trigger('wishlist:updateCount', {
                count: data.wishlistTotal
            });
        },
        complete: function () {
            // Shown whether or not the rollback call succeeded. The customer's
            // save failed either way, and telling them is more useful than
            // reporting on an internal compensation step.
            showFailure();
        }
    });
}

/**
 * Whether the compensating rollback is available on this storefront.
 *
 * @returns {boolean} true when FR-08 compensation should be armed
 */
function isCompensationEnabled() {
    return Boolean(settings().rollbackUrl);
}

/**
 * Announces the outcome of a save that authentication interrupted.
 *
 * The outcome was produced and cleared server-side on this page's request, so
 * this runs at most once per completed continuation.
 *
 * Every interrupted save, including "Save for Later", is complete by the time
 * this runs: the continuation copies the item and removes the cart line in the
 * same request, before any page renders. There is deliberately no client step
 * left. The previous arrangement handed the cart removal to the cart line
 * item's component, which works only when the customer lands back on the cart;
 * when they did not — a saved-items return marker or a registration redirect
 * sends them elsewhere — no component was found and this confirmed that the
 * item had moved while the cart line was still in the basket.
 *
 * @returns {void}
 */
function handleContinuation() {
    var result = settings().continuation;

    if (!result) {
        return;
    }

    if (result.status !== 'success') {
        showFailure();
        return;
    }

    showConfirmation(result);
}

/**
 * Path of the saved-items page, as the server built it.
 *
 * Resolved through an anchor so a root-relative URL, an absolute one and an
 * SEO alias all reduce to the same comparable path.
 *
 * @returns {string|null} the path, or null when the gate published no list URL
 */
function listPath() {
    var url = settings().listUrl;

    if (!url) {
        return null;
    }

    var resolver = document.createElement('a');
    resolver.href = url;

    return resolver.pathname;
}

/**
 * Whether a click asked for this document rather than a new tab or window.
 *
 * A modified click is left alone: the customer asked for the page somewhere
 * else, and opening a prompt over the page they are still looking at would
 * answer a question they did not ask. The new tab gets the prompt from the
 * route instead.
 *
 * @param {Object} event - the jQuery click event
 * @returns {boolean} true for an unmodified primary click
 */
function isPlainLeftClick(event) {
    return !(event.which > 1 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey);
}

/**
 * FR-01. Answers a request for the saved-items list with the prompt over the
 * current page, instead of a page whose body is the prompt.
 *
 * FR-06 requires a gated guest to be prompted when they ask for the list; it
 * does not say where. FR-01 does: the prompt appears on top of the page the
 * customer is on, and that page is left as it was.
 *
 * @returns {void}
 */
function initListGate() {
    var path = listPath();

    if (!path) {
        return;
    }

    // Bound once on the document rather than per link. The list is linked from
    // the header, the mobile navigation, the account left nav, the cart, the
    // save confirmation and the wishlist carousel, and those links share no
    // class; matching on the path covers all of them, and any added later,
    // without a marker class in a dozen shared templates.
    //
    // `required` is read on each click rather than here, so a session that
    // ended while the page sat open still gets the prompt.
    $(document).off('click.wishlistGate').on('click.wishlistGate', 'a[href]', function (event) {
        if (!isRequired() || !isPlainLeftClick(event) || this.pathname !== path) {
            return;
        }

        event.preventDefault();
        // The href is carried so a stale-gate answer can follow the link the
        // customer actually clicked rather than the configured list URL, which
        // may differ by query string.
        promptForList(this.href);
    });

    // A direct arrival -- a typed URL, a bookmark, a link in an email, a
    // restored tab -- never passes through the handler above, and is not
    // handled here either. `Wishlist-Show` sends a gated guest to the login
    // page, so there is no list page underneath for a prompt to sit over.
}

module.exports = {
    isRequired: isRequired,
    csrfField: csrfField,
    initListGate: initListGate,
    openPrompt: openPrompt,
    promptForList: promptForList,
    isCompensationEnabled: isCompensationEnabled,
    rollbackSaveForLater: rollbackSaveForLater,
    detectSurface: detectSurface,
    intercept: intercept,
    handleAuthenticationRequired: handleAuthenticationRequired,
    handleBulkSaveRefusal: handleBulkSaveRefusal,
    cancelPendingAction: cancelPendingAction,
    promptForAuthentication: promptForAuthentication,
    showConfirmation: showConfirmation,
    showFailure: showFailure,
    handleContinuation: handleContinuation
};
